import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import PurchaseOrderForm from './PurchaseOrderForm';
import ProjectSalesTaxWarningModal from '../ProjectSalesTaxWarningModal';
import API_BASE from '../../../utils/api.js';
import { hasProjectSalesTaxAssigned } from '../../../utils/projectSalesTax.js';
import usePermissions from '../../../auth/usePermissions';

export default function PurchaseOrderFormPage() {
  const { projectId, poId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const autoDownload = searchParams.get('autoDownload') === '1';
  const outlet = useOutletContext?.() || {};
  const [project, setProject] = useState(outlet.project || location.state?.project || null);
  const { permissions, permissionsLoaded } = usePermissions();

  // Queue of remaining draft PO ids created together (e.g. a proposal split
  // across several vendors). "Save & Close" advances into the next one
  // instead of leaving the rest for the user to hunt down individually.
  const poQueue = Array.isArray(location.state?.poQueue) ? location.state.poQueue : [];
  const poQueueTotal = location.state?.poQueueTotal || null;
  const poQueuePosition = location.state?.poQueuePosition || null;

  const advanceQueueOrClose = () => {
    if (poQueue.length > 0) {
      const [nextPoId, ...rest] = poQueue;
      navigate(`/projects/${projectId}/purchase-orders/${nextPoId}`, {
        replace: true,
        state: {
          project,
          poQueue: rest,
          poQueueTotal,
          poQueuePosition: poQueuePosition ? poQueuePosition + 1 : null,
        },
      });
    } else {
      navigate(`/projects/${projectId}/purchase-orders`);
    }
  };

  useEffect(() => {
    if (permissionsLoaded && !poId && permissions.createEditPurchaseOrders === false) {
      navigate(`/projects/${projectId}`, { replace: true });
    }
  }, [permissionsLoaded, permissions, projectId, poId, navigate]);

  const userId = localStorage.getItem('userId');

  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(Boolean(poId));
  const taxAlertShownRef = useRef(false);
  const [showTaxWarning, setShowTaxWarning] = useState(false);

  useEffect(() => {
    if (project || !projectId || !userId) return;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${userId}/${projectId}`, {
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('Failed to load project');
        const data = await res.json();
        setProject(data || null);
      } catch (err) {
        console.error('Failed to load project:', err);
      }
    })();
  }, [project, projectId, userId]);

  /* ----------------------------------------------------
     Load existing PO (edit mode)
  ----------------------------------------------------- */
  useEffect(() => {
    if (!poId || !userId) return;

    // Advancing the PO queue changes `poId` without remounting this page, so
    // reset state up front — otherwise the previous PO would flash/persist
    // while the next one loads.
    setLoading(true);
    setInitial(null);

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/purchase-orders/${userId}/by-id/${poId}`, {
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('Failed to load purchase order');
        const data = await res.json();
        setInitial(data);
      } catch (err) {
        console.error('Failed to load purchase order:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [API_BASE, poId, userId]);

  useEffect(() => {
    if (poId || !project || taxAlertShownRef.current) return;
    taxAlertShownRef.current = true;
    if (!hasProjectSalesTaxAssigned(project)) {
      setShowTaxWarning(true);
    }
  }, [poId, project]);

  if (loading) {
    return <div style={{ padding: 24 }}>Loading purchase order…</div>;
  }

  return (
    <>
      <PurchaseOrderForm
        project={project}
        initial={initial}
        autoDownload={autoDownload}
        onClose={() => navigate(-1)}
        onSaved={advanceQueueOrClose}
        queueRemaining={poQueue.length}
        queuePosition={poQueuePosition}
        queueTotal={poQueueTotal}
      />
      <ProjectSalesTaxWarningModal
        isOpen={showTaxWarning}
        docLabel="purchase order"
        onClose={() => setShowTaxWarning(false)}
        onEditProject={() =>
          navigate(`/projects/${encodeURIComponent(projectId)}/edit`, {
            state: { project },
          })
        }
      />
    </>
  );
}
